/**
 * Pure, three.js-free noise generation and terrain class-blend maths for the
 * splat-shaded terrain material (issue #61). Kept dependency-free so the
 * deterministic bake and the slope/altitude class weights are unit-testable in
 * Node, exactly like the other pure geometry builders in this codebase
 * (RouteRibbon / BuildingsLayer) are separated from their three.js wrapper.
 *
 * The bake is fully deterministic from a fixed seed, so the noise textures are
 * identical across sessions. This module NEVER touches LOS: it only produces
 * cosmetic surface-variation data consumed by terrainMaterial.ts.
 *
 * The blend cascade (continuous slope/altitude class weights composited
 * back-to-front) and the macro/meso/micro breakup-layer design are adapted from
 * Braffolk/fable5-world-demo `TerrainMaterial.ts` (MIT, commit fd75fdb) — see
 * outputs/qa/mining/05_visual_quality.md §3. The reference is WebGPU/TSL; here
 * the same maths are re-expressed for WebGL2 (CPU bake + GLSL onBeforeCompile).
 */

/** RGB triple, components in 0..1. */
export type RGB = [number, number, number];

function hexToRgb(hex: number): RGB {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

/**
 * Single source of truth for the terrain ground palette (sRGB hex), shared by
 * BOTH quality tiers: the high-tier GLSL splat path (via TERRAIN_CLASS_COLORS
 * below) and the low-tier vertex-colour path (terrainMaterial.ts derives its
 * ELEVATION_BANDS from this map, so the two can never drift — issue #90).
 *
 * Calibrated to reference photos of the Ray II area under overcast light
 * (issue #90). The defining property is **green ≥ red on every flat-ground
 * band at every altitude**: the landscape is a muted grey-green, not brown.
 * Colours grade from sage/olive greens low down to desaturated grey-green on
 * the high moor/ridge, with cool blue-grey rock/scree on steep faces (matching
 * the sampled crag `#505b61`). The previous heather/ridge/summit values were
 * warm browns (R > G) which, ramping in from ~208 m across most of the
 * 150-340 m AOI, made the uplands read brown (staging regression, issue #90).
 *
 * Sampled sRGB targets these were tuned against (region averages, overcast):
 *   pasture #838f6b · hay meadow #757c5e · rough grass #747e44 ·
 *   mid-distance fields #697456 · bracken #53675f · far moor #73817a ·
 *   crag rock #505b61. Targets are the *rendered* pixel (palette × lighting ×
 *   AgX × grade); the constants below sit distinctly greener than the targets
 *   (G/R ≈ 1.2 in sRGB vs ~1.1 sampled) on purpose: the scene's sun light is
 *   warm (`atmosphere.ts` sun ≈ {1, 0.949, 0.875}, out of this issue's scope)
 *   and AgX compresses saturation, so a palette merely as green as the target
 *   renders back to neutral R≈G at distance. Over-greening the source lands
 *   the *rendered* upland ridge/moor back in the target family with margin
 *   (measured in the terrainPalette e2e; see outputs/qa/ comparison).
 */
export const TERRAIN_PALETTE_HEX = {
  grass: 0x63793f, // valley / lower pasture — sage green
  moor: 0x687d47, // upland moor — olive-green
  heather: 0x66805a, // high moor (was heather-brown) — muted grey-green
  ridge: 0x6b8278, // ridge (was brown) — desaturated grey-green
  summit: 0x849a8c, // summit — light grey-green
  scree: 0x76898b, // talus / broken slope — cool grey
  rock: 0x54666d, // steep exposed rock face — cool blue-grey crag
} as const;

/**
 * Terrain class palette — a continuous-blend superset of the legacy hard
 * ELEVATION_BANDS, derived from the single TERRAIN_PALETTE_HEX source above.
 * grass/moor/heather/ridge/summit are the altitude bands the vertex-colour
 * path also uses; scree/rock are slope-driven classes the flat banding never
 * had. Both tiers therefore share identical hues (asserted by a unit test).
 */
export const TERRAIN_CLASS_COLORS = {
  grass: hexToRgb(TERRAIN_PALETTE_HEX.grass),
  moor: hexToRgb(TERRAIN_PALETTE_HEX.moor),
  heather: hexToRgb(TERRAIN_PALETTE_HEX.heather),
  ridge: hexToRgb(TERRAIN_PALETTE_HEX.ridge),
  summit: hexToRgb(TERRAIN_PALETTE_HEX.summit),
  scree: hexToRgb(TERRAIN_PALETTE_HEX.scree),
  rock: hexToRgb(TERRAIN_PALETTE_HEX.rock),
} as const;

/** Altitude transition edges (metres AOD). Centres match the legacy bands
 * (180/230/280/330); each has a soft half-width so the class change is a ramp,
 * not a contour line. */
export const ALTITUDE_EDGES = {
  moorLo: 158,
  moorHi: 202,
  heatherLo: 208,
  heatherHi: 252,
  ridgeLo: 258,
  ridgeHi: 302,
  summitLo: 308,
  summitHi: 352,
} as const;

/** Slope class edges. slope = 1 - clamp(abs(worldNormal.y)): 0 flat, ~0.29 at 45°,
 * 0.5 at 60°. Scree fades in on moderate slopes, rock takes over on steep. */
export const SLOPE_EDGES = {
  screeLo: 0.3,
  screeHi: 0.5,
  rockLo: 0.48,
  rockHi: 0.8,
} as const;

/** Accurate sRGB → linear transfer (matches three's default ColorManagement),
 * so palette colours authored as sRGB hex land in the renderer's linear working
 * space. The legacy vertex-colour path gets this for free via `new Color(hex)`;
 * the GLSL splat path assigns straight into `diffuseColor.rgb` (already linear),
 * so it must convert the palette itself. */
function srgbChannelToLinear(c: number): number {
  return c < 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function srgbToLinearRgb(rgb: RGB): RGB {
  return [srgbChannelToLinear(rgb[0]), srgbChannelToLinear(rgb[1]), srgbChannelToLinear(rgb[2])];
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mixRgb(a: RGB, b: RGB, t: number): RGB {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/**
 * Composited terrain albedo for an (altitude, slope) sample — the same
 * back-to-front mix() cascade the GLSL fragment shader runs, kept here as the
 * single source of truth for the class-weight boundaries so the shader and the
 * unit tests provably agree. `slope` is 1 - clamp(abs(worldNormal.y), 0, 1).
 */
export function terrainAlbedo(altitude: number, slope: number): RGB {
  const C = TERRAIN_CLASS_COLORS;
  const A = ALTITUDE_EDGES;
  const S = SLOPE_EDGES;
  let col = C.grass;
  col = mixRgb(col, C.moor, smoothstep(A.moorLo, A.moorHi, altitude));
  col = mixRgb(col, C.heather, smoothstep(A.heatherLo, A.heatherHi, altitude));
  col = mixRgb(col, C.ridge, smoothstep(A.ridgeLo, A.ridgeHi, altitude));
  col = mixRgb(col, C.summit, smoothstep(A.summitLo, A.summitHi, altitude));
  const rockW = smoothstep(S.rockLo, S.rockHi, slope);
  const screeW = smoothstep(S.screeLo, S.screeHi, slope) * (1 - rockW);
  col = mixRgb(col, C.scree, screeW);
  col = mixRgb(col, C.rock, rockW);
  return col;
}

/** 32-bit integer hash → [0,1). Deterministic and platform-stable (Math.imul). */
function hash2(ix: number, iy: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 1442695040)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Periodic value noise in [0,1). Wraps on `period` lattice cells in both axes so
 * the baked texture tiles seamlessly (belt-and-braces with MirroredRepeat).
 */
export function valueNoise2D(x: number, y: number, period: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const wrap = (i: number): number => ((i % period) + period) % period;
  const wx0 = wrap(x0);
  const wy0 = wrap(y0);
  const wx1 = wrap(x0 + 1);
  const wy1 = wrap(y0 + 1);
  const v00 = hash2(wx0, wy0, seed);
  const v10 = hash2(wx1, wy0, seed);
  const v01 = hash2(wx0, wy1, seed);
  const v11 = hash2(wx1, wy1, seed);
  return lerp(lerp(v00, v10, sx), lerp(v01, v11, sx), sy);
}

export interface FbmOptions {
  octaves: number;
  period: number;
  seed: number;
}

/** Fractal Brownian value noise in [0,1), periodic on `period` in the source
 * coordinate space (each octave doubles both frequency and lattice period). */
export function fbm2D(x: number, y: number, { octaves, period, seed }: FbmOptions): number {
  let sum = 0;
  let amp = 0.5;
  let ampSum = 0;
  let freq = 1;
  let per = period;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise2D(x * freq, y * freq, per, seed + o * 101);
    ampSum += amp;
    amp *= 0.5;
    freq *= 2;
    per *= 2;
  }
  return sum / ampSum;
}

/** Ridged fractal noise in [0,1): sharp ridges from folded value noise. */
export function ridged2D(x: number, y: number, { octaves, period, seed }: FbmOptions): number {
  let sum = 0;
  let amp = 0.5;
  let ampSum = 0;
  let freq = 1;
  let per = period;
  for (let o = 0; o < octaves; o++) {
    const n = valueNoise2D(x * freq, y * freq, per, seed + o * 101);
    sum += amp * (1 - Math.abs(2 * n - 1));
    ampSum += amp;
    amp *= 0.5;
    freq *= 2;
    per *= 2;
  }
  return sum / ampSum;
}

/** Default seed for the terrain noise bake. Fixed so the textures are identical
 * across sessions (acceptance: deterministic, seeded, stable). */
export const TERRAIN_NOISE_SEED = 0x2a17;

/** Lattice cell counts across the full texture per channel. Kept modest so the
 * texture stays low-frequency and cheap to filter on the low-power target. */
const R_CELLS = 8; // value noise → macro tint breakup
const G_CELLS = 6; // fbm → meso albedo band
const B_CELLS = 5; // ridged → micro near-camera bump

/**
 * Bakes an RGBA byte texture (size×size) carrying three tileable noise fields:
 * R = value noise, G = fbm, B = ridged, A = 255. Deterministic from `seed`.
 * Returned as a plain Uint8Array so the wrapper can drop it straight into a
 * three.js DataTexture; no renderer or DOM needed to produce it.
 */
export function generateTerrainNoiseData(size: number, seed: number = TERRAIN_NOISE_SEED): Uint8Array {
  const data = new Uint8Array(size * size * 4);
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const u = px / size;
      const v = py / size;
      const r = valueNoise2D(u * R_CELLS, v * R_CELLS, R_CELLS, seed + 11);
      const g = fbm2D(u * G_CELLS, v * G_CELLS, { octaves: 4, period: G_CELLS, seed: seed + 23 });
      const b = ridged2D(u * B_CELLS, v * B_CELLS, { octaves: 3, period: B_CELLS, seed: seed + 37 });
      const i = (py * size + px) * 4;
      data[i] = Math.round(r * 255);
      data[i + 1] = Math.round(g * 255);
      data[i + 2] = Math.round(b * 255);
      data[i + 3] = 255;
    }
  }
  return data;
}
